<script lang="ts" setup>
/* Stores */
import { useCityStore } from '@/store/city/city'

/* Types */
import { GiftProduct } from '@/types/api/api-v3/basket'

/* Components */
import AppMeasureUnits from '@/components/App/AppMeasureUnits.vue'

/* Async Components */
const BasketSelectedGiftIcon = defineAsyncComponent(
  () => import('@/components/Icons/IconSelectedGift.vue'),
)

type Props = {
  gift: GiftProduct
  isGiftSelected: boolean
  isGiftNeed: boolean
}

defineProps<Props>()

const emit = defineEmits(['selectGift'])

const { currencySymbol } = useCityStore()

const selectGift = () => emit('selectGift')
</script>

<template>
  <div
    class="gift"
    :class="{ 'gift--inactive': !isGiftNeed, 'gift--active': isGiftSelected }"
  >
    <div v-if="gift?.image" class="gift__image">
      <nuxt-img :src="gift.image" />
    </div>

    <div v-if="isGiftSelected" class="gift__check">
      <basket-selected-gift-icon />
    </div>

    <div class="gift__wrapper">
      <div class="gift__price">0 {{ currencySymbol }}</div>

      <app-measure-units
        class="gift__weight"
        :weight="gift?.weight"
        :volume-in-liters="gift?.volume_in_liters"
        :measure="gift?.measure"
      />
    </div>

    <div class="gift__title">
      {{ gift?.title }}
    </div>

    <div class="gift__button">
      <v-btn
        class="gift__button-item"
        :disabled="isGiftSelected || !isGiftNeed"
        @click="selectGift"
      >
        <span v-if="isGiftSelected && isGiftNeed">Выбрано</span>

        <app-icon v-else name="plus-icon" size="40" />
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.gift {
  position: relative;
  display: grid;
  grid-template-rows: 1fr max-content 1fr max-content;
  max-width: 176px;
  height: 247px;
  padding: 8px;
  background-color: $button-background;
  border: 2px solid transparent;
  border-radius: 16px;
  opacity: 1;
  transition: all 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
  scroll-snap-align: start;
  scroll-snap-stop: always;

  &--active {
    border: 2px solid $basket-success;
    box-shadow: 0 6px 30px 0 rgba(0, 197, 102, 0.22);
  }

  &--inactive {
    opacity: 0.5;
  }

  &__image img {
    width: 100%;
  }

  &__image {
    height: 102px;
    margin-bottom: 8px;
  }

  &__check {
    position: absolute;
    top: -10px;
    right: -5px;
  }

  &__wrapper {
    display: flex;
    gap: 4px;
    align-items: baseline;
  }

  &__price {
    font-size: 14px;
    font-weight: 800;
  }

  &__weight {
    font-size: 12px;
    font-weight: 600;
    line-height: 15px;
    color: $dark-grey;
  }

  &__title {
    display: -webkit-box;
    height: 45px;
    overflow: hidden;
    font-size: 12px;
    font-weight: 600;
    line-height: 15px;
    text-overflow: ellipsis;
    white-space: initial;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  &__button {
    width: 100%;
    height: 100%;
    margin-top: 16px;

    &-item {
      width: 100%;
      font-size: 12px;
      font-weight: 600;
      color: black !important;
      border-radius: 100px;
      box-shadow: none;
    }

    :deep(.v-btn__overlay) {
      background-color: $white;
      opacity: 0;
    }
  }

  @include media('<tablet') {
    max-width: 144px;
    height: max-content;
    max-height: 227px;

    &__image {
      height: 85px;
      margin-bottom: 8px;
    }

    &__check {
      top: 0;
    }

    &__button {
      min-width: 128px;
    }
  }
}
</style>
